<template>
  <div>
    <el-tabs v-model="menu">
      <el-tab-pane name="agreement">
        <template #label><span><i class="fa fa-file-signature text-muted"></i> Sharer Agreement</span></template>

        <el-row>
          <el-col :xs="24" :sm="12" :md="6">
            Agreement Start <strong>{{formatDate(details.started)}}</strong>
          </el-col>

          <el-col :xs="24" :sm="12" :md="6">
            Agreement Expiry <strong>{{formatDate(details.expiry)}}</strong>
          </el-col>

          <el-col :xs="24" :sm="12" :md="6">
            Agreement Term <strong v-if="term !== null">{{term}} months</strong><strong v-else>—</strong>
          </el-col>

          <el-col :xs="24" :sm="12" :md="6">
            Expires <strong>{{expiry !== null ? expiry : '—'}}</strong>
          </el-col>
        </el-row>

        <!-- XOL-462: strict_position_out_of_range / strict_term_exceeded banner.
             Deliberately OUTSIDE the <el-form> below — an alert inside an inline
             form reflows the input rows when it appears or disappears
             (ProjectTab.vue:12-17). -->
        <sharer-billing-issues :issues="billingIssues" />

        <p>
            <h3>Site Financials</h3>
            <el-button v-if="isManager('finance|project') && !readOnly" type="text" aria-label="Edit sharer agreement" @click="editState()"><i :class="editing ? 'fa fa-check-circle text-success' : 'fa fa-edit'"></i></el-button>
            <el-button v-if="this.editing" type="text" @click="editing = false"><i class="fa fa-times-circle text-danger"></i></el-button>
        </p>

        <!-- XOL-467: any-to-all auto-fill notice. Outside <el-form> for the same
             reason as the billing-issues banner above — appearing/disappearing
             must never reflow the inline input rows. No Handover input on this
             tab, so the notice only ever concerns Agreement Start / Billing
             Start. -->
        <el-alert v-if="editing && autoFilled" type="info" show-icon :closable="false"
          description="Billing Start was blank, so it has been filled in from the date you just set. Adjust it if it differs." />

        <el-row>
          <el-col :span="24">
            <el-form inline label-position="top" ref='financeForm' :model='details'>

              <el-row :gutter="12">
                <el-col :span="24">
                  <select-sharer-status v-model="details.status" :disabled="!editing" />

                  <span v-if="details.started || editing" class="handover-field">
                    <date-input label="Agreement Start" v-model="details.started" :disabled="!editing"
                      ref="dateField_started"
                      :class="[{'licensee-date--editing': editing}, {'licensee-date--handover-gutter': showHandoverAction}]"
                      :error="fieldErrors.started" @change="onDateChange('started', $event)" />
                    <!-- XOL-479: this tab has no Handover input (Carlos, 2026-09-01 —
                         do not add one here). When the block/backstop names this field
                         as the one to fix, send the user to the only screen that CAN
                         fix it, without losing this in-progress edit (see
                         openHandoverOnProjectTab). Rendered only for the exact
                         handover-required message — never for the other `started`
                         messages, which this tab can resolve locally. -->
                    <el-button v-if="showHandoverAction" type="text" size="small"
                      class="handover-field__action" @click="openHandoverOnProjectTab">
                      Open Project tab to set Handover <i class="fa fa-arrow-right" aria-hidden="true"></i>
                    </el-button>
                  </span>
                  <date-input v-if="details.status !== 'Terminated' && (details.startBilling || editing)" label="Billing Start" v-model="details.startBilling" :disabled="!editing"
                    ref="dateField_startBilling" :class="{'licensee-date--editing': editing}"
                    :error="fieldErrors.startBilling" @change="onDateChange('startBilling', $event)" />
                  <date-input v-if="details.status == 'Terminated' && (details.terminated || editing)" label="Terminated" v-model="details.terminated" :disabled="!editing"
                    :class="{'licensee-date--editing': editing}" @change="clearDateErrors" />
                  <date-input v-if="details.expiry || editing" label="Expiry" v-model="details.expiry" :disabled="!editing"
                    ref="dateField_expiry" :class="{'licensee-date--editing': editing}"
                    :error="fieldErrors.expiry" @change="onDateChange('expiry', $event)" />

                </el-col>
              </el-row>

              <!-- Sharer Rate on its own row + Duplicate & modify (XOL-241) -->
              <!-- XOL-355: hidden when the rate is manually overridden — the tiered
                   sharer rate is irrelevant while a flat override is in effect. -->
              <el-row v-if="!details.overrideEnabled" :gutter="12">
                <el-col :span="24">
                  <sharer-rate-override v-model="details.rate_id" :sharer-id="sharer.organisation_id" :disabled="!editing" @input="onRateChanged" />
                </el-col>
              </el-row>

              <!-- XOL-306: in-form version select (edit mode, version-managers only) -->
              <el-row v-if="editing && details.rate_id && canSwitchVersion && !details.overrideEnabled" :gutter="12">
                <el-col :sm="24" :md="16">
                  <el-form-item label="Rate version">
                    <select-rate-versions
                      :versions="rateVersions"
                      :value="selectedVersionId"
                      :loading="loadingVersions"
                      :disabled="!canSwitchVersion"
                      clearable
                      placeholder="Latest (unversioned)"
                      @input="onSelectVersion" />
                  </el-form-item>
                </el-col>
              </el-row>

              <!-- XOL-259 Phase 2 / XOL-306: read-only version summary (view mode) -->
              <!-- XOL-355: hidden when the rate is manually overridden. -->
              <el-row v-if="!details.overrideEnabled" :gutter="12">
                <el-col :span="24">
                  <el-divider content-position="left">Rate version</el-divider>
                  <sharer-rate-version v-show="!editing" ref="sharerRateVersion" :id="sharer.id" :rate-id="details.rate_id" />
                </el-col>
              </el-row>

              <!-- Transient note after a rate or version change — XOL-306 -->
              <el-alert
                v-if="resumingFee"
                type="info"
                :closable="false"
                show-icon
                title=""
                description="Fees are recalculating in the background — this may take a moment." />

              <el-alert v-if="details && editing" type="warning" title="Sharer specific!" description="Only input values PER sharer. For site wide CAPEX and OPEX see Finance Summary." show-icon  :closable="false" />
                <!-- XOL-319: vertical gap between the Rate version table and the Calculated (auto) rate -->
                <el-row :gutter="12" style="margin-top: 16px;">
                    <el-col :span="24">

                      <!-- XOL-268 Phase B: Current Fees + override controls -->
                      <currency-input
                        v-if="details.overrideEnabled"
                        label="Current Fees"
                        v-model="details.currentRate"
                        :disabled="!editing || !canOverride"
                        placeholder="Enter manual fee" />

                      <!-- Override checkbox — only visible when editing -->
                      <el-tooltip
                        v-if="editing"
                        content="Only finance users can override the rate."
                        placement="bottom"
                        :disabled="canOverride">
                        <check-box
                          v-if="editing"
                          class="override-lock-label"
                          :label="'Override/Lock\nRate'"
                          v-model="details.overrideEnabled"
                          :disabled="!canOverride" />
                      </el-tooltip>

                      <!-- Non-manager helper text (guaranteed visible fallback) -->
                      <p v-if="editing && !canOverride" class="text-muted" style="font-size:12px;margin:0 0 8px;">
                        Only finance users can override the rate.
                      </p>

                      <!-- Calculated (auto) rate display — XOL-268 -->
                      <el-form-item v-if="!details.overrideEnabled" label="Calculated (auto) rate">
                        <span v-if="details.calculatedRate !== null && details.calculatedRate !== undefined">{{calcRateDisplay}}</span>
                        <span v-else>
                          <span>—</span>
                          <span class="text-muted" style="font-size:12px;margin-left:6px;">No calculated rate yet (no rate set, no handover, or past the last tier).</span>
                        </span>
                      </el-form-item>

                      <!-- Transient note after clearing override — XOL-268 -->
                      <el-alert
                        v-if="resumingAuto"
                        type="info"
                        :closable="false"
                        show-icon
                        title=""
                        description="Resuming the calculated rate — this may take a moment to update." />

                      <float-input
                        v-if="details.discount || editing"
                        v-model="details.discount"
                        label="Discount" :prefix="details.discountPercentage ? currency : ''"
                        :suffix="details.discountPercentage ? '' : '%'"
                        :disabled="!editing" />

                        <check-box
                          v-if="editing || details.discount"
                          label="Disc. Type"
                          v-model="details.discountPercentage"
                          :disabled="!editing">
                          {{!details.discountPercentage ? '%' : currency}}
                        </check-box>

                        <float-input
                            v-if="details.interestRate || editing"
                            v-model="details.interestRate"
                            label="Interest Rate"
                            suffix="%"
                            :disabled="!editing" />

                      <float-input v-if="details.capex || editing" label="Sharer Specific CAPEX" v-model="details.capex" :decimals="2" :disabled="!editing" />
                      <float-input v-if="details.opex || editing" label="Sharer Specific OPEX" v-model="details.opex" :decimals="2" :disabled="!editing"  />
                </el-col>

              </el-row>

              <div v-if="details.updated_at">
                <small class="text-muted pull-right">Last update: <b>{{formatFriendlyDate(details.updated_at)}}</b><span v-if="details.updated_by"> by {{details.updated_by}}</span></small>
              </div>

          </el-form>

        </el-col>
      </el-row>

      <el-row v-if="canAccess('finance') && sharer.site_id && sharer.id">
        <el-col :span="24">
          <files-panel title="License Agreement" type="site" :id="sharer.site_id" process="sharer" :process-id="sharer.id" hide-carousel />
        </el-col>
      </el-row>

   </el-tab-pane>


   <el-tab-pane name="sharerInvoice" lazy>
     <template #label><span><i class="fa fa-money-check text-muted"></i> Sharer Invoice</span></template>

     <site-invoice :site-data="siteData" :sharer-id="sharer.organisation_id" :org="sharer" />
   </el-tab-pane>

   <el-tab-pane name="sharerVO" lazy>
     <template #label><span><i class="fa fa-screwdriver text-muted"></i> Sharer V.O.</span></template>

     <site-invoice :site-data="siteData" :sharer-id="sharer.organisation_id" :org="sharer" is-vo />
   </el-tab-pane>


   <el-tab-pane name="billableEquip" lazy>
     <template #label><span><i class="fa fa-tools text-muted"></i> Billable Equip.</span></template>
     <billable-equip-table :site-id="details.site_id" :site-data="siteData" :owner-id="sharer.organisation_id" />
   </el-tab-pane>

   <el-tab-pane name="feeSchedule" lazy>
     <template #label><span><i class="fa fa-calendar-alt text-muted"></i> Fee Schedule</span></template>
     <sharer-fee-schedule :id="sharer.id" :started="details.started" />
   </el-tab-pane>

   <el-tab-pane name="feeTimeline" lazy>
     <template #label><span><i class="fa fa-stream text-muted"></i> Fee Timeline</span></template>
     <sharer-fee-timeline :id="sharer.id" :started="details.started" />
   </el-tab-pane>

 </el-tabs>
  </div>
</template>


<script>

import axios from '@/lib/axiosClient'
import PostMixins from '@/mixins/PostMixins'
import NumberMixins from '@/mixins/NumberMixins'
import DateMixins from '@/mixins/DateMixins'
import DateChainMixin from '@/mixins/DateChainMixin'
import {dateChainErrors, autoFillDates} from '@/lib/licenseeDates'
import {toAppTime, appNow} from '@/lib/appTime'
import {pushDrawer} from '@/lib/layerNav'


import {settings} from '@/components/Data'

import SelectSharerStatus from '@/components/atoms/selects/SelectSharerStatus'
import SelectRateVersions from '@/components/atoms/selects/SelectRateVersions'
import SharerRateOverride from '@/components/finance/rates/SharerRateOverride'

import BillableEquipTable from '@/components/assets/subcat/BillableEquipTable'

import SiteInvoice from '@/components/sites/finance/SiteInvoice'
import SharerFeeSchedule from './SharerFeeSchedule'
import SharerFeeTimeline from './SharerFeeTimeline'
import SharerRateVersion from './SharerRateVersion'
import SharerBillingIssues from './SharerBillingIssues'

// XOL-479: single source of truth for the handover-required copy — used by
// both the client-side block (dateChainCopy) and the 422 backstop
// (onSaveErrors), and compared against by `showHandoverAction` to decide
// whether the "open Project tab" affordance renders. Do NOT add a Handover
// input to this tab (Carlos, 2026-09-01) — the fix lives on the Project tab;
// this only gets the user there without losing their edit.
var HANDOVER_REQUIRED_MESSAGE = 'This sharer has no Handover date — set it on the Project tab before saving a date change.'

// XOL-467 (any-to-all widening): Agreement Start <-> Billing Start only —
// this tab has no Handover input, so Handover is neither source nor target
// here (Carlos, 2026-09-01). See licenseeDates.js's autoFillDates() doc.
var DATE_CHAIN_FIELDS = ['started', 'startBilling']

export default {
  mixins: [DateMixins, NumberMixins, PostMixins, DateChainMixin],

  props: {
    sharer: {
      type: Object,
      required: true
    },

    editMode: {
      type: Boolean,
      default: false
    },

    siteData: {
      type: Object
    },

    readOnly: {
      type: Boolean,
      default: false
    }
  },

  components: {
    BillableEquipTable,
    SelectSharerStatus,
    SelectRateVersions,
    SiteInvoice,
    SharerRateOverride,
    SharerFeeSchedule,
    SharerFeeTimeline,
    SharerRateVersion,
    SharerBillingIssues,
  },

  computed: {
    // Both are rendered unconditionally in the header, and `expiry` is
    // genuinely optional (line 42 gates its own input on `details.expiry ||
    // editing`). toAppTime returns null where the old moment(null) returned an
    // *invalid* moment — which absorbed .diff()/.fromNow() as NaN/'Invalid
    // date' — so these must guard or the whole tab throws.
    term: function () {
      var oExpiry = toAppTime(this.details.expiry)
      var oStarted = toAppTime(this.details.started)
      return (oExpiry === null || oStarted === null) ? null : oExpiry.diff(oStarted, 'months')
    },

    expiry: function () {
      var oExpiry = toAppTime(this.details.expiry)
      return (oExpiry === null) ? null : oExpiry.from(appNow())
    },

    // XOL-371 review 2026-07-21: reverted to a manager gate — only finance|project
    // managers may set an override (not read-only). Matches the edit-toggle gate.
    canOverride: function () {
      return this.isManager('finance|project') && !this.readOnly
    },

    // XOL-259 Phase 2 / XOL-371 review 2026-07-21: gates the version switcher —
    // reverted to a finance|project manager gate (not read-only), same as canOverride.
    canSwitchVersion: function () {
      return this.isManager('finance|project') && !this.readOnly
    },

    // XOL-268: formatted calculated rate, dash when null/undefined
    calcRateDisplay: function () {
      var v = this.details.calculatedRate
      if (v === null || v === undefined) {
        return '—'
      }
      return this.formatMoney(v, 2)
    },

    // XOL-479: gates the "Open Project tab to set Handover" affordance —
    // exact-match against the shared constant so it never lights up for a
    // DIFFERENT message that happens to also land on `started`
    // (started_required).
    showHandoverAction: function () {
      return this.fieldErrors.started === HANDOVER_REQUIRED_MESSAGE
    }
  },

  methods: {
    // XOL-462: modelled on ProjectTab.loadBillingMode() — a silent side-read.
    // Never invent a clean state from a failed fetch: leave billingIssues at
    // whatever it already was (data() seeds []).
    loadBillingIssues: function () {
      if (!this.details.id) return
      var self = this
      this.fetchData(
        'licensees/' + this.details.id + '/fee-schedule',
        function (data) {
          var meta = data && data.meta
          self.billingIssues = (meta && Array.isArray(meta.issues)) ? meta.issues : []
        },
        'billing issues',
        function () { /* leave empty — never invent a clean state from a failed fetch */ }
      )
    },

    // XOL-268: derive overrideEnabled — prefer the explicit field, fall back to rateLocked
    deriveOverride: function (obj) {
      if (obj && obj.overrideEnabled !== undefined && obj.overrideEnabled !== null) {
        return !!obj.overrideEnabled
      }
      return !!(obj && obj.rateLocked)
    },

    // XOL-268: fold response from PUT /rate-override into local state + reset baselines
    applyOverrideResponse: function (data) {
      if (!data) return
      if (data.currentRate !== undefined) {
        this.details.currentRate = data.currentRate
      }
      if (data.calculatedRate !== undefined) {
        this.details.calculatedRate = data.calculatedRate
      }
      var derived = this.deriveOverride(data)
      this.details.overrideEnabled = derived
      this.originalRate = data.currentRate
      this.originalOverride = derived
    },

    // XOL-268: PUT /licensees/{id}/rate-override
    saveRateOverride: function () {
      var self = this

      // Guard: must have a valid positive rate when override is enabled
      var rate = Number(this.details.currentRate)
      if (!this.details.currentRate || isNaN(rate) || rate <= 0) {
        this.$message({
          type: 'warning',
          title: 'Missing fee',
          message: 'Enter a Current Fee amount before overriding the calculated rate.'
        })
        this.editing = true
        return false
      }

      var path = '/licensees/' + this.details.id + '/rate-override'
      var url = this.getEndpoint(path)

      this.send(
        self,
        url,
        { currentRate: rate },
        'Rate override',
        function (data) {
          self.applyOverrideResponse(data)
        },
        null,
        axios.put,
        true,
        'Save'
      )
      return true
    },

    // XOL-268: DELETE /licensees/{id}/rate-override
    clearRateOverride: function () {
      var self = this

      var path = '/licensees/' + this.details.id + '/rate-override'
      var url = this.getEndpoint(path)

      this.send(
        self,
        url,
        null,
        'Rate override',
        function (data) {
          self.details.overrideEnabled = false
          self.originalOverride = false
          self.resumingAuto = true
          // Do NOT copy stale data.currentRate — let calculatedRate govern display
          if (data && data.calculatedRate !== undefined) {
            self.details.calculatedRate = data.calculatedRate
          }
        },
        null,
        axios.delete,
        true,
        'Save'
      )
    },

    // XOL-306: load published versions for a rate; autoLatest=true pins selectedVersionId
    // to the current version automatically (on rate change), autoLatest=false seeds
    // the existing pin from originalVersionId (on edit-open).
    loadVersionsForRate: function (rateId, autoLatest) {
      var self = this
      // Only version-managers manage the pin; the /rates/{id}/versions endpoint is
      // gated finance|admin|management, so skip the fetch for finance|project-only
      // editors (avoids a 403 + spurious error toast). Their save omits
      // rate_version_id (see commitFinance) so the backend auto-pins on rate change.
      if (!this.canSwitchVersion) {
        this.rateVersions = []
        this.selectedVersionId = this.originalVersionId
        return
      }
      if (!rateId) {
        this.rateVersions = []
        this.selectedVersionId = null
        return
      }
      this.loadingVersions = true
      this.fetchData(
        'rates/' + rateId + '/versions',
        function (data) {
          self.rateVersions = Array.isArray(data) ? data : []
          if (autoLatest) {
            var current = self.rateVersions.find(function (v) { return v.is_current })
            self.selectedVersionId = current ? current.id : null
          } else {
            self.selectedVersionId = self.originalVersionId
          }
          self.loadingVersions = false
        },
        'rate versions',
        function (_err) {
          self.rateVersions = []
          self.selectedVersionId = autoLatest ? null : self.originalVersionId
          self.loadingVersions = false
        }
      )
    },

    // XOL-306: called by @input on sharer-rate-override; auto-defaults version to latest
    onRateChanged: function (rateId) {
      this.loadVersionsForRate(rateId, true)
    },

    // XOL-306: called by @input on select-rate-versions in edit mode
    onSelectVersion: function (id) {
      this.selectedVersionId = (id === undefined || id === null) ? null : id
    },

    // XOL-268 Phase B: extracted write sequence — receives already-computed booleans
    commitFinance: function (overrideChanged, rateChanged, rateIdChanged, versionPinNeeded) {
      if (this.details.overrideEnabled && (overrideChanged || rateChanged)) {
        if (this.saveRateOverride() === false) return  // empty-fee guard: keep editing, no bulk save
      } else if (!this.details.overrideEnabled && overrideChanged) {
        this.clearRateOverride()
      }
      var self = this
      var payload = this.clone(this.details)
      delete payload.currentRate
      delete payload.rateLocked
      // XOL-416: this payload is a blocklist off the full decorateSharer body, so it
      // echoed the sharer's billing slot back like the sharer tabs did. The slot is
      // not editable on this screen, so it is not sent.
      //
      // This is a real guard, not tidiness: it is what stops a RATE edit from moving
      // a sharer's BILLING POSITION as a side effect. Deleting the line would let any
      // stale `sharerNo` in `details` ride along on a save that has nothing to do
      // with it.
      //
      // (XOL-431 removed the neighbouring `delete payload.billing_no`. That column no
      // longer exists, so it stripped a key that was never in the body.)
      delete payload.sharerNo
      // XOL-306/XOL-312: the bulk PUT auto-pins ONLY when rate_id changes — send the
      // version-manager's chosen pin (id, or null = unpin) on a rate change so it's
      // atomic + honoured. A same-rate version switch is ignored by the bulk PUT, so
      // it's persisted via the dedicated re-pin endpoint in the success callback.
      // finance|project-only editors omit the field entirely (backend auto-pins).
      delete payload.rate_version_id
      if (this.canSwitchVersion && rateIdChanged) {
        payload.rate_version_id = this.selectedVersionId
      }
      // XOL-467: no Handover input on this tab — never echo it back.
      // Sending it unconditionally would re-arm LicenseeRequest's row-level
      // chain invariant on every unrelated save (a pure rate change), which
      // is exactly the lockout the dirty-gate exists to remove for the ~72
      // Active licensees that already violate it. `started` / `startBilling`
      // / `expiry` are dirty-gated too — sent only when actually edited.
      delete payload.handover
      if (this.details.started === this.originalStarted) delete payload.started
      if (this.details.startBilling === this.originalStartBilling) delete payload.startBilling
      if (this.details.expiry === this.originalExpiry) delete payload.expiry
      this.saveForm(this, '/licensees', this.details.id, payload, 'Sharer Financial Data', function (resp) {
        if (rateIdChanged) {
          self.originalRateId = self.details.rate_id
        }
        self.originalStarted = self.details.started
        self.originalStartBilling = self.details.startBilling
        self.originalExpiry = self.details.expiry
        self.autoFilled = false
        // Same-rate version change → the bulk PUT ignores rate_version_id when the
        // rate is unchanged (XOL-312), so persist it via the dedicated re-pin
        // endpoint (XOL-259). (Same-rate unpin to null isn't persistable — the
        // endpoint requires an id — so that edge is skipped.)
        if (self.canSwitchVersion && !rateIdChanged && versionPinNeeded && self.selectedVersionId !== null) {
          self.repinVersion(self.selectedVersionId)
        } else {
          self.originalVersionId = self.selectedVersionId
          if (resp) self.onVersionRepinned(resp)
          // Reload the read-only summary after a rate change (its pin/rate moved);
          // a no-op save needs no refresh.
          if (rateIdChanged && self.$refs.sharerRateVersion) self.$refs.sharerRateVersion.load()
        }
        // Transient fee-recalc note when rate or version changed (async recompute)
        if (rateIdChanged || versionPinNeeded) {
          self.resumingFee = true
          // XOL-462: a rate or version change can create or clear a strict
          // violation — re-read the issues banner.
          self.loadBillingIssues()
        }
      }, function (errors) {
        // XOL-479: 422 backstop — see onSaveErrors().
        self.onSaveErrors(errors)
      })
      this.editing = false
    },

    // XOL-306: persist a SAME-RATE version switch via the dedicated re-pin endpoint
    // (the bulk PUT only auto-pins on a rate change — XOL-312). Mirrors the former
    // SharerRateVersion.doSwitch; folds the decorated licensee + refreshes the summary.
    repinVersion: function (versionId) {
      var self = this
      this.send(
        this,
        this.getEndpoint('/licensees/' + this.details.id + '/rate-version'),
        { rate_version_id: versionId },
        'Rate version',
        function (licensee) {
          self.originalVersionId = versionId
          if (licensee) self.onVersionRepinned(licensee)
          if (self.$refs.sharerRateVersion) self.$refs.sharerRateVersion.load()
        },
        function (_err) {
          // error toast handled by send; the read-only summary reflects server truth
        },
        axios.put,
        true,
        'Save',
        null,
        true
      )
    },

    // XOL-268 Phase B: compose the $confirm body from the applicable change bullets
    buildImpactMessage: function (overrideOn, overrideOff, rateChange, versionChange) {
      var items = []
      if (overrideOn) {
        items.push('<li>This sharer’s fee will be locked at the manual amount and the scheduled rate recalculation will skip this sharer until the override is removed.</li>')
      }
      if (overrideOff) {
        items.push('<li>This sharer’s fee will return to the calculated (automatic) rate and will recompute on its own — this may take a moment to appear.</li>')
      }
      if (rateChange) {
        items.push('<li>This sharer’s fees will be calculated from the new rate’s tiers going forward; past records are not affected.</li>')
      }
      if (versionChange) {
        if (this.selectedVersionId) {
          items.push('<li>This sharer will be pinned to a specific rate version; future fees use that version’s frozen tiers and recompute in the background — this may take a moment.</li>')
        } else {
          items.push('<li>This sharer will follow the current (live) rate again; its fee recomputes in the background.</li>')
        }
      }
      return '<p>Before saving, please note:</p><ul style="margin:0;padding-left:20px;text-align:left;">' + items.join('') + '</ul>'
    },

    // XOL-259: fold the re-pin response into local details so the fee display refreshes
    onVersionRepinned: function (licensee) {
      if (!licensee) return
      if (licensee.calculatedRate !== undefined) {
        this.details.calculatedRate = licensee.calculatedRate
      }
      if (licensee.currentRate !== undefined) {
        this.details.currentRate = licensee.currentRate
      }
      if (licensee.rateVersion !== undefined) {
        this.details.rateVersion = licensee.rateVersion
      }
      if (licensee.updated_at !== undefined) {
        this.details.updated_at = licensee.updated_at
      }
      if (licensee.updated_by !== undefined) {
        this.details.updated_by = licensee.updated_by
      }
    },

    editState: function () {
      if (this.editing) {
        this.details.started = this.formatDateSql(this.details.started)
        this.details.startBilling = this.formatDateSql(this.details.startBilling)
        this.details.expiry = this.formatDateSql(this.details.expiry)
        this.details.terminated = this.formatDateSql(this.details.terminated)

        // XOL-467: mirror LicenseeRequest::withValidator()'s own early
        // return — the API only checks the chain when the save actually
        // TOUCHES a date. Skipping validateDates() here when nothing dirty-
        // gated changed is what lets a pure rate/override edit save on the
        // ~72 Active licensees that already violate the chain (an Agreement
        // Start, no Handover) — the dirty-gate's whole reason for existing.
        //
        // Blocking, field-level, above BOTH exits below — the early
        // `commitFinance` a few lines down (when `needsConfirm` is false) and
        // the `.then(commitFinance)` after the fee-change confirm. A
        // date-only edit sets every fee flag false and leaves by the early
        // exit, so gating only the confirm path would let it bypass
        // validation entirely — the exact edit this exists to catch.
        if (this.chainFieldsDirty() && !this.validateDates()) return

        var overrideChanged = this.details.overrideEnabled !== this.originalOverride
        var rateChanged = Number(this.details.currentRate) !== Number(this.originalRate)
        var rateIdChanged = this.details.rate_id !== this.originalRateId
        var versionIdChanged = this.canSwitchVersion && (this.selectedVersionId !== this.originalVersionId)

        var overrideOn = this.details.overrideEnabled && (overrideChanged || rateChanged)
        var overrideOff = !this.details.overrideEnabled && overrideChanged
        var rateChange = rateIdChanged
        var needsConfirm = overrideOn || overrideOff || rateChange || versionIdChanged

        if (!needsConfirm) {
          this.commitFinance(overrideChanged, rateChanged, rateIdChanged, versionIdChanged)
          return
        }

        var self = this
        this.$confirm(
          this.buildImpactMessage(overrideOn, overrideOff, rateChange, versionIdChanged),
          'Confirm fee change',
          {
            confirmButtonText: 'Save changes',
            cancelButtonText: 'Keep editing',
            center: true,
            dangerouslyUseHTMLString: true,
            type: 'warning'
          }
        ).then(function () {
          self.commitFinance(overrideChanged, rateChanged, rateIdChanged, versionIdChanged)
        }).catch(function () {
          // cancel: write nothing, stay in edit mode
        })
      } else {
        this.editing = true
        this.resumingFee = false
        this.clearDateErrors()
        this.autoFilled = false
        // XOL-306: seed the version selector when entering edit mode
        if (this.details.rate_id) {
          this.loadVersionsForRate(this.details.rate_id, false)
        }
      }
    },

    // XOL-467: field -> message for `dateChainErrors()`. This tab has no
    // `handover` input — the chain's ROOT rule reroutes onto Agreement
    // Start, the field whose presence made the requirement true, naming
    // where to actually fix it. Site → Financials cannot repair its own
    // block; the dirty-gate is what removes the urgency (an unrelated rate
    // change still saves).
    dateChainCopy: function (item) {
      switch (item.code) {
        case 'handover_required':
          return {
            field: 'started',
            message: HANDOVER_REQUIRED_MESSAGE
          }
        case 'started_required':
          return {field: 'started', message: 'Required when Billing Start is set.'}
        case 'billing_start_required':
          return {field: 'startBilling', message: 'Required when Expiry is set.'}
        case 'billing_before_started':
          return {
            field: 'startBilling',
            message: 'Cannot be before Agreement Start (' + this.formatDate(this.details.started) + ').'
          }
        case 'expiry_before_billing_start':
          return {field: 'expiry', message: 'Must be after Billing Start.'}
        default:
          return null
      }
    },

    // XOL-467: true when this save would actually SEND one of the three date
    // keys commitFinance() dirty-gates (`handover` is never sent from this
    // tab at all). Mirrors LicenseeRequest::withValidator()'s own "touches no
    // date" early return.
    chainFieldsDirty: function () {
      return this.details.started !== this.originalStarted ||
        this.details.startBilling !== this.originalStartBilling ||
        this.details.expiry !== this.originalExpiry
    },

    // XOL-467: synchronous, hand-rolled — Element's own validate() is
    // unusable here (`v-model="details"`, not `:model`), and this form's
    // four date-inputs are keyless `v-if` siblings that get patch-reused on
    // a status flip (the XOL-451 always-passes trap Element's validate()
    // would fall into).
    validateDates: function () {
      var self = this
      var errors = dateChainErrors(this.details)
      var map = {}

      errors.forEach(function (item) {
        var copy = self.dateChainCopy(item)
        if (copy && !map[copy.field]) {
          map[copy.field] = copy.message
        }
      })

      this.fieldErrors = map

      var fields = Object.keys(map)
      if (fields.length > 0) {
        this.notifyBlockedSave(fields.length)
        this.focusDateField(fields[0])
        return false
      }

      return true
    },

    // Errors clear on ANY date @change — the whole map, since the rules are
    // cross-field. Also checks for Agreement Start <-> Billing Start
    // auto-fill (XOL-467) — `expiry` changes route through here too but are
    // never a member of DATE_CHAIN_FIELDS, so they no-op in maybeAutoFillDates.
    onDateChange: function (field, value) {
      this.clearDateErrors()
      if (!this.editing) return
      this.maybeAutoFillDates(field, value)
    },

    // XOL-467 (any-to-all widening): Agreement Start and Billing Start can
    // each source the other — this tab has no Handover input. Fires only
    // when the other field was empty before this edit — the same gate
    // doubles as the "do not fight the user" re-arm condition (see
    // autoFillDates()'s own doc in licenseeDates.js). Mutates `details`
    // directly, so a fill IS a genuine edit `chainFieldsDirty()` sees and
    // `commitFinance()` sends.
    maybeAutoFillDates: function (field, value) {
      var updates = autoFillDates(this.details, field, value, DATE_CHAIN_FIELDS)
      var keys = Object.keys(updates)
      if (keys.length === 0) return

      var self = this
      keys.forEach(function (key) { self.details[key] = updates[key] })
      this.autoFilled = true
    },

    // XOL-479: 422 backstop. validateDates() blocks everything the CLIENT can
    // see, but this tab's `details.handover` is a stale snapshot taken when
    // the component mounted — a concurrent edit on the Project tab (someone
    // else clears Handover between mount and Save) is invisible to it. The
    // API is the source of truth and re-checks the same chain
    // (LicenseeRequest::withValidator() in xolas-tms-api), returning the
    // error keyed on `handover` (its own root field — this tab reroutes onto
    // `started`, the same as the client-side path). PostMixins.send already
    // raises one $notify per Laravel validation error; this only adds the
    // SAME actionable affordance the client-side block shows, keyed off the
    // same message constant so `showHandoverAction` lights up either way.
    onSaveErrors: function (errors) {
      if (!errors || !errors.handover) return
      this.fieldErrors = Object.assign({}, this.fieldErrors, { started: HANDOVER_REQUIRED_MESSAGE })
      this.focusDateField('started')
    },

    // XOL-479: navigate to this licensee's Project tab (the only screen with
    // a Handover input) without losing the in-progress Financials edit.
    // `licensee` stacks as an L2 drawer ON TOP of the L1 `site` drawer this
    // component lives inside (LicenseeDetailLayer, drawerRegistry.js) — the
    // L1 route/component identity is unchanged by pushDrawer, so THIS
    // component is not unmounted and `details`/`editing` survive naturally.
    // Verified in src/components/atoms/layers/DrawerStack.vue: the layer-host
    // v-for is keyed on `entry.name + ':' + entry.args.id`, which does not
    // change when an L2 entry is appended.
    openHandoverOnProjectTab: function () {
      if (!this.sharer || !this.sharer.id || !this.$router) return
      pushDrawer(this.$router, { open: { licensee: { id: this.sharer.id, tab: 'project' } } })
    }
  },

  watch: {
    details: function (data) {
      this.details = data
    }
  },

  data: function () {
    var derived = this.deriveOverride(this.sharer)
    return {
      menu: 'agreement',
      editing: this.editMode,
      currency: settings.currency,
      details: Object.assign(this.clone(this.sharer), { overrideEnabled: derived }),
      originalRate: this.sharer.currentRate,
      originalOverride: derived,
      originalRateId: this.sharer.rate_id,
      // XOL-364: coerce to Number — the bulk-sharers payload serializes
      // rate_version_id as a string ("4"), but SelectRateVersions' options have
      // numeric ids and el-select matches by strict equality, so a string pin
      // matches no option and the selector renders the raw id instead of the label.
      originalVersionId: (this.sharer.rate_version_id != null && this.sharer.rate_version_id !== '') ? Number(this.sharer.rate_version_id) : null,
      // XOL-467: dirty-gate baselines for the date chain — see commitFinance().
      originalStarted: this.sharer.started,
      originalStartBilling: this.sharer.startBilling,
      originalExpiry: this.sharer.expiry,
      // XOL-467: any-to-all auto-fill notice flag — see maybeAutoFillDates().
      autoFilled: false,
      rateVersions: [],
      selectedVersionId: null,
      loadingVersions: false,
      resumingAuto: false,
      resumingFee: false,
      billingIssues: []
    }
  },

  mounted: function () {
    this.loadBillingIssues()
  }
}

</script>

<style scoped>
/* XOL-367: tighten the two-line "Override/Lock Rate" toggle label. Element's
   .el-form-item__label is line-height:40px, so two lines would be 40px taller
   than the single-line sibling labels — opening a large gap AND dropping the
   checkbox a row below the other inputs. line-height:20px makes the two lines
   occupy 40px (== one sibling line), so the gap tightens and the checkbox
   realigns with the Current Fees / Discount / Interest Rate inputs. */
.override-lock-label ::v-deep .el-form-item__label {
  line-height: 20px;
}

/* XOL-467: reserve room for the absolutely-positioned .el-form-item__error so
   a blocked-save message can never overlap the Sharer Rate row below.
   Applied via a parent-supplied class (not a bare ::v-deep .el-form-item rule
   — that leaks into every nested form, XOL-451) while editing, to every date
   field in the row so a message appearing/disappearing on one field never
   reflows the others. */
.licensee-date--editing {
  margin-bottom: 56px !important;
}

.licensee-date--editing ::v-deep .el-form-item__error {
  line-height: 1.4;
}

/* XOL-479: wraps the Agreement Start date-input so the "Open Project tab to
   set Handover" button can be positioned relative to it. Inline-block to
   match how el-form-item sits in this inline form (Element's own
   inline-form CSS), so wrapping it here does not change the row's flow. */
.handover-field {
  position: relative;
  display: inline-block;
  vertical-align: top;
}

/* The button sits BELOW the (up to 2-line) .el-form-item__error, inside the
   reserved gutter — .licensee-date--editing's 56px is tuned for the error
   text alone, so this field gets a taller reserve on top of it whenever the
   affordance is showing, extended enough that the button never overlaps the
   Sharer Rate row beneath (XOL-467's original overlap concern). */
.licensee-date--handover-gutter {
  margin-bottom: 94px !important;
}

.handover-field__action {
  position: absolute;
  left: 0;
  /* Centred in the band between the label text and the input's top border.
     Measured, not guessed: the label's glyph box ends 28px down the wrapper
     and the input starts at 50px, so the band is 22px for a 15px-tall button
     — only 7px of slack. 32px centres it: 4px clear of each.
       top:38  -> bottom 53, 3px INSIDE the input (sat on the border)
       top:28  -> flush against the label text, 0px above it
       top:32  -> 4px below the label, 4px above the input
     If the label's type size changes, re-measure the glyph box (a Range over
     the label's contents) rather than the padded element box — the element is
     50px tall and reading that is what produced the first two wrong values. */
  top: 32px;
  padding: 0;
}
</style>
